import { useMemo } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, AlertTriangle, DollarSign, Package, Info } from 'lucide-react';
import { calculateTotalCharges, MANDATORY_CHARGES } from '@/constants/commercialLogic';

export const ChargeBreakdown = ({
  containers,
  lineHaul,
  zone,
  appointmentType,
  dropDate,
  pickupDate,
  showDetails,
  onToggleDetails,
  isAdmin = false  // Only admins can see Line Haul
}) => {
  const breakdown = useMemo(() => {
    return calculateTotalCharges({
      containers,
      lineHaul,
      zone,
      appointmentType,
      dropDate,
      pickupDate
    });
  }, [containers, lineHaul, zone, appointmentType, dropDate, pickupDate]);

  if (!breakdown) return null;

  const numContainers = containers.length;

  // Calculate accessorial-only total for customers (excluding Line Haul)
  const accessorialTotal = breakdown.subtotalAccessorials;

  return (
    <div className="bg-[#0F172A] border border-white/10 rounded-lg overflow-hidden" data-testid="charge-breakdown">
      {/* Header */}
      <button
        onClick={onToggleDetails}
        className="w-full flex items-center justify-between p-4 bg-[#1E293B]/50 hover:bg-[#1E293B]/70 transition-colors"
      >
        <div className="flex items-center gap-2">
          <DollarSign className="text-[#00A6E8]" size={20} />
          <span className="text-white font-semibold">Charges Summary</span>
          <span className="text-slate-400 text-sm">({numContainers} container{numContainers > 1 ? 's' : ''})</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xl font-bold text-[#00A6E8]">
            ${accessorialTotal}
          </span>
          {showDetails ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
        </div>
      </button>

      {/* Details */}
      {showDetails && (
        <div className="p-4 space-y-4">
          {/* Customer Notice - Line Haul will be quoted separately */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <Info className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
              <div className="text-sm">
                <p className="text-blue-400 font-semibold">Accessorial Charges Summary</p>
                <p className="text-slate-400 text-xs mt-1">
                  The total shown reflects accessorial charges only. Line Haul will be quoted by DLT operations team after review.
                </p>
              </div>
            </div>
          </div>

          {/* Per-Container Breakdown - Show only accessorials */}
          {numContainers > 1 && (
            <div className="mb-4">
              <p className="text-[#00A6E8] text-xs uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
                <Package size={14} />
                Per-Container Charges
              </p>
              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                {breakdown.containers.map((c, i) => {
                  // Calculate accessorial-only total per container
                  const containerAccessorials = c.chassisMinimum + c.chassisSplit + c.overweight + c.hazmat + c.dropCharge;
                  return (
                    <div key={i} className="bg-[#1E293B]/30 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-medium">{c.containerNumber}</span>
                        <span className="text-[#00A6E8] font-bold">${containerAccessorials}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="text-slate-400">Chassis: ${c.chassisMinimum + c.chassisSplit}</span>
                        {c.overweight > 0 && (
                          <span className="text-yellow-400">OW: +${c.overweight}</span>
                        )}
                        {c.hazmat > 0 && (
                          <span className="text-yellow-400">HAZ: +${c.hazmat}</span>
                        )}
                        {c.dropCharge > 0 && (
                          <span className="text-orange-400">Drop: +${c.dropCharge}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary Table - Accessorials Only */}
          <div className="border-t border-white/10 pt-4">
            <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold mb-3">
              Charge Details
            </p>

            {/* Mandatory Charges */}
            <div className="py-2 border-b border-white/5">
              <p className="text-[#FFA726] text-xs uppercase tracking-wider mb-2 flex items-center gap-1">
                <CheckCircle size={12} />
                Mandatory Charges (per container)
              </p>
              
              <div className="flex justify-between items-center text-sm mb-1">
                <div>
                  <span className="text-slate-300">Chassis Split</span>
                  <span className="text-slate-500 ml-2">
                    (${MANDATORY_CHARGES.chassis_split.rate} × {numContainers})
                  </span>
                </div>
                <span className="text-white">${breakdown.chassisSplit.amount}</span>
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <div>
                  <span className="text-slate-300">Chassis Minimum (2 days)</span>
                  <span className="text-slate-500 ml-2">
                    (${MANDATORY_CHARGES.chassis_minimum.rate} × {numContainers})
                  </span>
                </div>
                <span className="text-white">${breakdown.chassisMinimum.amount}</span>
              </div>
            </div>

            {/* Auto-Applied Charges */}
            {(breakdown.overweight.count > 0 || breakdown.hazmat.count > 0 || breakdown.dropCharge.count > 0) && (
              <div className="py-2 border-b border-white/5">
                <p className="text-yellow-400 text-xs uppercase tracking-wider mb-2 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  Auto-Applied Charges
                </p>
                
                {breakdown.overweight.count > 0 && (
                  <div className="flex justify-between items-center text-sm mb-1">
                    <div>
                      <span className="text-slate-300">Overweight (≥44,000 lbs)</span>
                      <span className="text-slate-500 ml-2">
                        ($175 × {breakdown.overweight.count})
                      </span>
                    </div>
                    <span className="text-yellow-400">${breakdown.overweight.amount}</span>
                  </div>
                )}

                {breakdown.hazmat.count > 0 && (
                  <div className="flex justify-between items-center text-sm mb-1">
                    <div>
                      <span className="text-slate-300">Hazmat</span>
                      <span className="text-slate-500 ml-2">
                        ($175 × {breakdown.hazmat.count})
                      </span>
                    </div>
                    <span className="text-yellow-400">${breakdown.hazmat.amount}</span>
                  </div>
                )}

                {breakdown.dropCharge.count > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <div>
                      <span className="text-slate-300">Drop Charge</span>
                      <span className="text-slate-500 ml-2">
                        ($50 × {breakdown.dropCharge.count})
                      </span>
                    </div>
                    <span className="text-orange-400">${breakdown.dropCharge.amount}</span>
                  </div>
                )}
              </div>
            )}

            {/* Total - Accessorials Only for Customers */}
            <div className="pt-4 mt-2">
              <div className="flex justify-between items-center mb-3">
                <span className="text-white font-semibold text-lg">ACCESSORIAL TOTAL:</span>
                <span className="text-[#00A6E8] font-bold text-2xl">${accessorialTotal}</span>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-3 mt-3">
                <div className="flex items-start gap-2">
                  <Info className="text-slate-400 flex-shrink-0 mt-0.5" size={14} />
                  <p className="text-xs text-slate-400">
                    <span className="text-white font-medium">Line Haul:</span> Will be quoted by DLT operations team.
                    You will receive a complete quotation once your submission is reviewed.
                  </p>
                </div>
              </div>
              
              <p className="text-xs text-slate-500 pt-3 mt-2 border-t border-white/5">
                *Estimated charges. Final quotation will be issued after DLT approval.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
